import React, { useState, useEffect, useContext, useCallback } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext"; // Adjust the path as needed
import { FaUserPlus, FaListAlt, FaInfoCircle, FaSearch } from "react-icons/fa";
import toast from "react-hot-toast";

const { REACT_APP_BACKEND_URL } = process.env;

const TenantManager = () => {
  const { authState } = useContext(AuthContext);
  const [tenants, setTenants] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [licenseType, setLicenseType] = useState("Free");
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [tenantsPerPage] = useState(3);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${REACT_APP_BACKEND_URL}/api/tenants/all-tenants`,
        {
          headers: {
            "x-auth-token": authState.token,
          },
        }
      );
      console.log("tenant data retrurned from the server;", response.data);
      setTenants(response.data);
      setFetchError("");
    } catch (error) {
      setFetchError("Failed to fetch tenants");
      console.error("Error fetching tenants:", error);
    } finally {
      setLoading(false);
    }
  }, [authState.token]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${REACT_APP_BACKEND_URL}/api/tenants/create-tenant`,
        { name, description, licenseType },
        {
          headers: {
            "x-auth-token": authState.token,
          },
        }
      );
      setName("");
      setDescription("");
      setLicenseType("Free");
      toast.success(response.data.message || "Tenant created successfully");
      await fetchTenants(); // Refresh the tenant list
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create tenant");
      console.error("Error creating tenant:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filtering tenants based on the search term
  const filteredTenants = tenants.filter((tenant) =>
    tenant.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination Logic
  const indexOfLastTenant = currentPage * tenantsPerPage;
  const indexOfFirstTenant = indexOfLastTenant - tenantsPerPage;
  const currentTenants = filteredTenants.slice(
    indexOfFirstTenant,
    indexOfLastTenant
  );
  const totalPages = Math.ceil(filteredTenants.length / tenantsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="mt-20 max-w-6xl mx-auto p-4 bg-white shadow-sm rounded-lg space-y-4">
      {/* Header Section */}
      <div className="p-4 bg-gray-100 rounded-lg shadow-sm mb-4 flex items-center space-x-3">
        <FaInfoCircle className="text-3xl text-black" />
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Tenant Management
          </h1>
          <p className="mt-1 text-gray-600 text-sm">
            Manage your tenants, add new ones, and view existing tenants with
            pagination.
          </p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row space-y-4 lg:space-y-0 lg:space-x-4">
        {/* Create Tenant Form */}
        <div className="flex-1 p-4 bg-gray-100 rounded-lg shadow-sm border border-gray-300">
          <h2 className="text-xl font-semibold mb-3 text-black flex items-center">
            <FaUserPlus className="mr-2 text-blue-600" /> Create Tenant
          </h2>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-800"
              >
                Name
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md py-2 px-3 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50"
                required
              />
            </div>
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-800"
              >
                Description
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50"
                rows="3"
                required
              />
            </div>
            <div>
              <label
                htmlFor="licenseType"
                className="block text-sm font-medium text-gray-800"
              >
                License Type
              </label>
              <select
                id="licenseType"
                value={licenseType}
                onChange={(e) => setLicenseType(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md py-2 px-3 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50"
              >
                <option value="Free">Free</option>
                <option value="Pro">Pro</option>
                <option value="Enterprise">Enterprise</option>
              </select>
            </div>
            <button
              type="submit"
              className={`w-full px-4 py-3 font-semibold text-white bg-black rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring focus:ring-blue-300 focus:ring-opacity-50 ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={loading}
            >
              {loading ? "Creating..." : "Create Tenant"}
            </button>
          </form>
        </div>
        {/* Tenant List */}
        <div className="flex-1 p-4 bg-gray-50 rounded-lg shadow-sm border border-gray-300">
          <h2 className="text-xl font-semibold mb-3 text-black flex items-center">
            <FaListAlt className="mr-2 text-green-600" /> Tenant List
          </h2>

          <div className="relative mb-4">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-full shadow-sm  focus:ring focus:ring-blue-500 focus:ring-opacity-50"
            />
            <FaSearch className="absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-400" />
          </div>
          {fetchError && (
            <div className="text-red-600 mb-2 text-sm">{fetchError}</div>
          )}
          {loading ? (
            <div className="text-center py-2 text-sm">Loading...</div>
          ) : (
            <div>
              <ul className="space-y-3">
                {currentTenants.length > 0 ? (
                  currentTenants.map((tenant) => (
                    <li
                      key={tenant._id}
                      className="p-3 border border-gray-200 rounded-md shadow-sm bg-white"
                    >
                      <h3 className="text-md font-semibold text-black">
                        {tenant.name}
                      </h3>
                      <p className="text-gray-700 text-sm">
                        {tenant.description}
                      </p>
                      <p className="text-gray-500 text-xs">
                        License Key: {tenant.licenseKey}
                      </p>
                      <p className="text-gray-500 text-xs">
                        License Type: {tenant.licenseType}
                      </p>
                      <p className="text-gray-500 text-xs">
                        Created At:{" "}
                        {new Date(tenant.createdAt).toLocaleDateString()}
                      </p>
                      <p className="text-gray-500 text-xs">
                        Expiration Date:{" "}
                        {new Date(tenant.expirationDate).toLocaleDateString()}
                      </p>
                      <p className="text-gray-500 text-xs">
                        Features: {tenant.features.join(", ")}
                      </p>
                    </li>
                  ))
                ) : (
                  <div className="text-gray-600 text-sm">No tenants found</div>
                )}
              </ul>

              {/* Pagination Controls */}
              <div className="flex justify-center mt-4 space-x-1">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  className={`relative px-2 py-1 border text-xs font-medium rounded-md ${
                    currentPage === 1
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-900 border-gray-300 hover:bg-gray-100"
                  }`}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, index) => (
                  <button
                    key={index}
                    onClick={() => handlePageChange(index + 1)}
                    className={`relative px-2 py-1 border text-xs font-medium rounded-md ${
                      currentPage === index + 1
                        ? "bg-blue-600 text-white"
                        : "bg-white text-gray-900 border-gray-300 hover:bg-gray-100"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  className={`relative px-2 py-1 border text-xs font-medium rounded-md ${
                    currentPage === totalPages
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-900 border-gray-300 hover:bg-gray-100"
                  }`}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantManager;
